import React from 'react'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * ChangeLogTicker (W5) — the How-It-Works step-4 vignette: recent rate changes
 * drifting softly upward on a seamless loop. The row list is DUPLICATED and the
 * track translates -50% (mkt3-ticker), so the second copy slides into the exact
 * position the first vacated — no visible seam. Fade masks top and bottom keep
 * rows appearing/disappearing behind soft edges.
 *
 * This is the ONE continuous animation in the How-It-Works section — every other
 * widget there animates once on reveal and then rests.
 *
 * Copy is VERBATIM from docs/marketing/2026-07-05-landing3-copy.md §W5 — do not
 * edit strings here without updating that doc + the claims register. Reasons are
 * plain-English surface phrasing (no stage class names); no compute cadence.
 * Figures are illustrative marketing fiction, not live rate data — the "no
 * frontend math" product rule does not apply (nothing is computed here; the
 * old→new pairs are literal copy).
 *
 * prefers-reduced-motion → the static list, no scroll (a designed still).
 */

interface ChangeRow {
  date: string
  room: string
  from: number
  to: number
  reason: string
}

// Verbatim from copy doc §W5 (date · room · old→new · reason)
const CHANGE_ROWS: ChangeRow[] = [
  { date: '14 Jul', room: 'Garden Double', from: 112, to: 120, reason: 'Weekend premium' },
  { date: '14 Jul', room: 'Standard Twin', from: 96, to: 101, reason: 'Booking pace ahead of history' },
  { date: '13 Jul', room: 'Garden Double', from: 128, to: 120, reason: 'Eased back inside guardrails' },
  { date: '13 Jul', room: 'Family Room', from: 140, to: 150, reason: 'Local event weekend' },
  { date: '12 Jul', room: 'Standard Twin', from: 101, to: 95, reason: 'Softer midweek demand' },
  { date: '12 Jul', room: 'Garden Double', from: 120, to: 120, reason: 'Override held' },
]

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

function Row({ row }: { row: ChangeRow }) {
  const rose = row.to < row.from
  const flat = row.to === row.from
  return (
    <li className="flex items-center gap-3 px-5 py-3 sm:gap-4">
      <span className="w-[3.4rem] shrink-0 font-mono text-[11px] tabular-nums text-[var(--mkt3-muted)]">{row.date}</span>
      <span className="min-w-0 flex-1 truncate text-sm text-[var(--mkt3-ink)]">{row.room}</span>
      <span className="flex shrink-0 items-center gap-1.5 font-mono text-[13px] tabular-nums">
        <span className="text-slate-400">£{row.from}</span>
        <ArrowRight aria-hidden="true" className="h-3 w-3 text-slate-300" />
        <span
          className={cn(
            'font-semibold',
            flat ? 'text-slate-500' : rose ? 'text-rose-600' : 'text-emerald-600'
          )}
        >
          £{row.to}
        </span>
      </span>
      <span className="hidden w-[13rem] shrink-0 truncate text-right text-xs text-[var(--mkt3-muted)] lg:block">
        {row.reason}
      </span>
    </li>
  )
}

export default function ChangeLogTicker({ className }: { className?: string }) {
  const [reduced] = React.useState(prefersReducedMotion)

  return (
    <div
      className={cn('mkt3-panel overflow-hidden', className)}
      role="img"
      aria-label="A change log of recent rate changes — each row shows the date, the room, the old and new price, and the reason."
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-[var(--mkt3-hairline)] px-5 py-3.5">
        <span className="mkt3-label">Change Log</span>
        <span aria-hidden="true" className="mkt3-live-dot h-1.5 w-1.5 rounded-full bg-teal-500 shadow-[0_0_8px_rgba(13,148,136,0.7)]" />
      </div>

      {/* Scrolling body — masked top/bottom */}
      <div
        aria-hidden={reduced ? undefined : true}
        className="relative h-[13.5rem] overflow-hidden"
        style={
          reduced
            ? undefined
            : {
                maskImage: 'linear-gradient(180deg, transparent, #000 16%, #000 84%, transparent)',
                WebkitMaskImage: 'linear-gradient(180deg, transparent, #000 16%, #000 84%, transparent)',
              }
        }
      >
        {reduced ? (
          <ul className="divide-y divide-[var(--mkt3-hairline)]">
            {CHANGE_ROWS.map((row, i) => (
              <Row key={i} row={row} />
            ))}
          </ul>
        ) : (
          <ul className="mkt3-ticker divide-y divide-[var(--mkt3-hairline)]">
            {/* Duplicated set: the -50% loop lands copy #2 exactly where copy #1 began */}
            {[...CHANGE_ROWS, ...CHANGE_ROWS].map((row, i) => (
              <Row key={i} row={row} />
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
